import React, { useState, memo } from 'react';
import { Card, Button, Tag, Typography, Divider, Space, InputNumber, Select, Tooltip, Input } from 'antd';
import { DollarOutlined, LinkOutlined, HistoryOutlined, MailOutlined, PrinterOutlined, EditOutlined, SaveOutlined, CloseOutlined } from '@ant-design/icons';
import { WorkOrder } from '../../types';
import { Invoice } from '../../services/paymentService';
import FeesEditor from '../Shared/FeesEditor';
import { useCapability } from '../auth/RoleGuard';

const { Text } = Typography;
const R: React.FC<{ label: string; value: string; fs?: number; color?: string; fw?: number }> = ({ label, value, fs, color, fw }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
    <Text style={{ fontSize: fs || 12, color: color || '#6b7280', fontWeight: fw }}>{label}</Text>
    <Text style={{ fontSize: (fs || 12) + 1, color, fontWeight: fw }}>{value}</Text>
  </div>
);
const btnStyle = { fontSize: 13, height: 36, fontWeight: 500 as const };

export interface PricingData {
  laborSubtotal: number; laborDiscounts: number; partsSubtotal: number; partsDiscounts: number;
  lineItemsTotal: number; jobDiscountAmount: number; subtotal: number; taxRate: number; taxAmount: number;
  total: number; paid: number; balanceDue: number; partsCost: number; partsRevenue: number; partsMargin: number;
  approvedItems: any[];
}

export interface WorkOrderFinancialsProps {
  workOrder: WorkOrder; pricing: PricingData; isMobile: boolean; isLocked: boolean;
  canSeeCost: boolean; canEditTaxRate: boolean; invoice: Invoice | null;
  onTaxRateChange: (rate: number) => Promise<void>; onTaxRateCancel: () => void;
  onJobDiscountSave: (type: string | null, value: number | null) => Promise<void>;
  onApplyPromo: (code: string) => Promise<void>; onRemovePromo: () => Promise<void>;
  onPaymentModal: () => void; onPaymentLinkModal: () => void; onPaymentHistoryModal: () => void;
  onInvoiceModal: () => void; onSendInvoiceEmail: () => void; onDownloadInvoicePDF: () => void;
  onSendInvoiceToSelf?: () => void;
  onDownloadApprovalPDF: () => void; startEditing: () => void; stopEditing: () => void;
  signatureData?: string; signedAt?: string; signatureType?: string; estimateId?: number;
}

const WorkOrderFinancials: React.FC<WorkOrderFinancialsProps> = ({
  workOrder, pricing, isMobile, canSeeCost, canEditTaxRate, invoice,
  onTaxRateChange, onTaxRateCancel, onJobDiscountSave, onApplyPromo, onRemovePromo,
  onPaymentModal, onPaymentLinkModal, onPaymentHistoryModal,
  onInvoiceModal, onSendInvoiceEmail, onDownloadInvoicePDF, onSendInvoiceToSelf, onDownloadApprovalPDF,
  startEditing, stopEditing, signatureData, signedAt, signatureType, estimateId,
}) => {
  const canApplyDiscounts = useCapability('apply_discounts');
  const [showLaborBreakdown, setShowLaborBreakdown] = useState(false);
  const [showPartsBreakdown, setShowPartsBreakdown] = useState(false);
  const [editingTaxRate, setEditingTaxRate] = useState(false);
  const [tempTaxRate, setTempTaxRate] = useState<number>(0);
  const [editingJobDiscount, setEditingJobDiscount] = useState(false);
  const [tempDiscountType, setTempDiscountType] = useState<string | null>(null);
  const [tempDiscountValue, setTempDiscountValue] = useState<number | null>(null);
  const [promoInput, setPromoInput] = useState('');
  const [promoBusy, setPromoBusy] = useState(false);

  const promoApplied = workOrder.promo_code_id != null;
  const handleApplyPromo = async () => {
    const code = promoInput.trim();
    if (!code) return;
    setPromoBusy(true);
    try { await onApplyPromo(code); setPromoInput(''); }
    finally { setPromoBusy(false); }
  };
  const handleRemovePromo = async () => {
    setPromoBusy(true);
    try { await onRemovePromo(); }
    finally { setPromoBusy(false); }
  };
  const diagnosisCredit = (workOrder.diagnosis_credit_cents ?? 0) / 100;

  const handleEditTaxRate = (e?: React.MouseEvent) => { e?.preventDefault(); e?.stopPropagation(); setTempTaxRate(workOrder.tax_rate ?? 7); setEditingTaxRate(true); startEditing(); };
  const handleSaveTaxRate = async (e?: React.MouseEvent) => { e?.preventDefault(); e?.stopPropagation(); await onTaxRateChange(tempTaxRate); setEditingTaxRate(false); stopEditing(); };
  const handleCancelEditTaxRate = (e?: React.MouseEvent) => { e?.preventDefault(); e?.stopPropagation(); setEditingTaxRate(false); setTempTaxRate(workOrder.tax_rate ?? 7); onTaxRateCancel(); stopEditing(); };

  const handleSaveJobDiscount = async () => {
    const type = tempDiscountType || workOrder.discount_type || null;
    const value = tempDiscountValue ?? workOrder.discount_value ?? 0;
    await onJobDiscountSave((value ?? 0) > 0 ? type : null, (value ?? 0) > 0 ? value : null);
    setEditingJobDiscount(false);
  };

  const DiscountBreakdown: React.FC<{ items: any[]; field: string }> = ({ items, field }) => (
    <div style={{ paddingLeft: 12, borderLeft: '2px solid #e5e7eb', display: 'flex', flexDirection: 'column', gap: 4 }}>
      {items.map((item: any, idx: number) => {
        const amt = field === 'labor' ? (item.labor_discount_amount || 0) : (item.parts || []).reduce((s: number, p: any) => s + (p.discount_amount || 0), 0);
        if (amt <= 0) return null;
        return <div key={idx} style={{ display: 'flex', justifyContent: 'space-between' }}><Text style={{ fontSize: 11, color: '#9ca3af' }}>{item.concern || `Item ${idx + 1}`}</Text><Text style={{ fontSize: 11, color: '#22c55e' }}>-${amt.toFixed(2)}</Text></div>;
      })}
    </div>
  );

  const pad = isMobile ? '14px' : '16px';
  const payDisabled = !workOrder.customer?.id || workOrder.total_amount <= 0;

  // Fees live in the stored WO total but not in the client-side `pricing` breakdown.
  // Derive them from the authoritative stored total so the panel reconciles exactly:
  // storedTotal = subtotal(after discount) + fees + tax  ⇒  fees = storedTotal − subtotal − tax.
  //
  // Guard against fabricating a phantom "Fees" row: when a WO has a stale stored
  // total_amount but NO approved line items (subtotal == 0), back-solving would
  // invent a fee equal to the whole orphan total (and a bogus 100% margin). Only
  // derive a fee when there is a real breakdown to reconcile against — i.e. there
  // are approved line items / a non-zero subtotal. Explicit fee records are shown
  // separately by the FeesEditor below.
  const storedTotal = workOrder.total_amount ?? pricing.total;
  const hasLineItems = (pricing.approvedItems?.length ?? 0) > 0 || pricing.subtotal > 0;
  const feesTotal = hasLineItems
    ? Math.max(0, Math.round((storedTotal - pricing.subtotal - pricing.taxAmount) * 100) / 100)
    : 0;
  const storedBalance = (workOrder as any).balance_due ?? pricing.balanceDue;

  return (
    <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: isMobile ? 12 : 16 }}>
      <Card title={<span style={{ fontSize: 13 }}>Financial Summary</span>} className="shadow-sm" styles={{ body: { padding: pad } }} style={{ overflow: 'hidden' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <R label="Labor Subtotal" value={`$${pricing.laborSubtotal.toFixed(2)}`} />
          {pricing.laborDiscounts > 0 && (<>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }} onClick={() => setShowLaborBreakdown(!showLaborBreakdown)}>
              <Text style={{ fontSize: 12, color: '#22c55e' }}>Labor Discounts {showLaborBreakdown ? '\u25B2' : '\u25BC'}</Text>
              <Text style={{ fontSize: 13, color: '#22c55e' }}>-${pricing.laborDiscounts.toFixed(2)}</Text>
            </div>
            {showLaborBreakdown && <DiscountBreakdown items={pricing.approvedItems.filter((i: any) => (i.labor_discount_amount || 0) > 0)} field="labor" />}
          </>)}
          <R label="Parts Subtotal" value={`$${pricing.partsSubtotal.toFixed(2)}`} />
          {pricing.partsDiscounts > 0 && (<>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }} onClick={() => setShowPartsBreakdown(!showPartsBreakdown)}>
              <Text style={{ fontSize: 12, color: '#22c55e' }}>Parts Discounts {showPartsBreakdown ? '\u25B2' : '\u25BC'}</Text>
              <Text style={{ fontSize: 13, color: '#22c55e' }}>-${pricing.partsDiscounts.toFixed(2)}</Text>
            </div>
            {showPartsBreakdown && <DiscountBreakdown items={pricing.approvedItems} field="parts" />}
          </>)}
          <Divider style={{ margin: '4px 0' }} />
          <R label="Line Items Total" value={`$${pricing.lineItemsTotal.toFixed(2)}`} fs={13} fw={600} />
          {/* Job Discount */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <Text style={{ fontSize: 12, color: pricing.jobDiscountAmount > 0 ? '#22c55e' : '#9ca3af' }}>Job Discount</Text>
              {canApplyDiscounts && (
                <EditOutlined style={{ fontSize: 10, color: '#9ca3af', cursor: 'pointer' }} onClick={() => { setTempDiscountType(workOrder.discount_type || 'percentage'); setTempDiscountValue(workOrder.discount_value ?? 0); setEditingJobDiscount(true); }} />
              )}
            </div>
            <Text style={{ fontSize: 13, color: pricing.jobDiscountAmount > 0 ? '#22c55e' : '#9ca3af' }}>{pricing.jobDiscountAmount > 0 ? `-$${pricing.jobDiscountAmount.toFixed(2)}` : '\u2014'}</Text>
          </div>
          {editingJobDiscount && canApplyDiscounts && (
            <Card size="small" style={{ marginTop: 8, border: '1px solid #1890ff' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <Select value={tempDiscountType || workOrder.discount_type || 'percentage'} onChange={setTempDiscountType} style={{ width: '100%' }} size="small">
                  <Select.Option value="percentage">Percentage Off (%)</Select.Option>
                  <Select.Option value="amount">Dollar Amount Off ($)</Select.Option>
                  <Select.Option value="final_price">Set Final Price ($)</Select.Option>
                </Select>
                <InputNumber value={tempDiscountValue ?? workOrder.discount_value ?? 0} onChange={(val) => setTempDiscountValue(val || 0)} min={0} max={(tempDiscountType || workOrder.discount_type) === 'percentage' ? 100 : undefined} precision={2} prefix={(tempDiscountType || workOrder.discount_type) === 'percentage' ? '' : '$'} suffix={(tempDiscountType || workOrder.discount_type) === 'percentage' ? '%' : ''} style={{ width: '100%', fontSize: 16 }} size="small" />
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}><Button size="small" onClick={() => setEditingJobDiscount(false)}>Cancel</Button><Button size="small" type="primary" onClick={handleSaveJobDiscount}>Save</Button></div>
              </div>
            </Card>
          )}
          {/* Promo code — apply replaces manual discount (backend handles either/or) */}
          {canApplyDiscounts && (
            promoApplied ? (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                <Space size={4}>
                  <Text style={{ fontSize: 12, color: '#6b7280' }}>Promo</Text>
                  <Tag color="green" style={{ margin: 0 }}>Applied</Tag>
                </Space>
                <Button size="small" danger type="link" loading={promoBusy} onClick={handleRemovePromo} style={{ padding: 0 }}>Remove</Button>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <Input
                  size="small"
                  placeholder="Promo code"
                  value={promoInput}
                  onChange={(e) => setPromoInput(e.target.value)}
                  onPressEnter={handleApplyPromo}
                  disabled={promoBusy}
                  style={{ flex: 1 }}
                />
                <Button size="small" type="primary" onClick={handleApplyPromo} loading={promoBusy} disabled={!promoInput.trim()}>Apply</Button>
              </div>
            )
          )}
          <Divider style={{ margin: '4px 0' }} />
          <R label="Subtotal" value={`$${pricing.subtotal.toFixed(2)}`} />
          {/* Tax */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {editingTaxRate && canEditTaxRate ? (<>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Text style={{ fontSize: 12, color: '#6b7280' }}>Tax Rate:</Text>
                <InputNumber min={0} max={100} step={0.01} value={tempTaxRate} onChange={(v) => setTempTaxRate(v || 0)} formatter={(v) => `${v}%`} parser={(v) => parseFloat(v!.replace('%', '')) || 0} style={{ width: 90, fontSize: 16 }} size="small" />
              </div>
              <Space size="small">
                <Button size="small" type="primary" htmlType="button" icon={<SaveOutlined />} onClick={handleSaveTaxRate} style={{ padding: '0 8px' }} />
                <Button size="small" htmlType="button" icon={<CloseOutlined />} onClick={handleCancelEditTaxRate} style={{ padding: '0 8px' }} />
              </Space>
            </>) : (<>
              {canEditTaxRate ? (
                <Tooltip title="Click to edit tax rate"><div style={{ fontSize: 12, color: '#6b7280', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }} onClick={handleEditTaxRate} onMouseEnter={(e) => e.currentTarget.style.color = '#3b82f6'} onMouseLeave={(e) => e.currentTarget.style.color = '#6b7280'}>Tax ({pricing.taxRate.toFixed(2)}%)<EditOutlined style={{ fontSize: 10 }} /></div></Tooltip>
              ) : <Text style={{ fontSize: 12, color: '#6b7280' }}>Tax ({pricing.taxRate.toFixed(2)}%)</Text>}
              <Text style={{ fontSize: 13 }}>${pricing.taxAmount.toFixed(2)}</Text>
            </>)}
          </div>
          {feesTotal > 0 && <R label="Fees" value={`$${feesTotal.toFixed(2)}`} />}
          {diagnosisCredit > 0 && <R label="Diagnosis credit" value={`−$${diagnosisCredit.toFixed(2)}`} color="#22c55e" />}
          <Divider style={{ margin: '4px 0', borderTopWidth: 2, borderColor: '#d1d5db' }} />
          {/* TOTAL - Visual focal point (authoritative stored total, includes fees) */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text style={{ fontSize: 20, fontWeight: 600, color: '#111827' }}>TOTAL</Text>
            <Text style={{ fontSize: 20, fontWeight: 600, color: 'var(--primary-600, #2563eb)' }}>${storedTotal.toFixed(2)}</Text>
          </div>
          {pricing.paid > 0 && <R label="Paid" value={`$${pricing.paid.toFixed(2)}`} color="#22c55e" />}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text style={{ fontSize: 16, fontWeight: 700, color: storedBalance <= 0 ? '#22c55e' : '#ef4444' }}>BALANCE DUE</Text>
            <Text style={{ fontSize: 16, fontWeight: 700, color: storedBalance <= 0 ? '#22c55e' : '#ef4444' }}>${storedBalance.toFixed(2)}</Text>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text style={{ fontSize: 12, color: '#6b7280' }}>Status</Text>
            <Tag color={workOrder.payment_status === 'paid' ? 'green' : workOrder.payment_status === 'partial' ? 'orange' : workOrder.payment_status === 'payment_plan' ? 'blue' : 'default'} style={{ fontSize: 11, padding: '2px 8px' }}>{(workOrder.payment_status || 'unpaid').toUpperCase()}</Tag>
          </div>
          <Divider style={{ margin: '8px 0' }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Button type="primary" icon={<DollarOutlined />} block onClick={onPaymentModal} disabled={payDisabled} style={btnStyle}>Take Payment</Button>
            <Button icon={<LinkOutlined />} block onClick={onPaymentLinkModal} disabled={payDisabled} style={btnStyle}>Generate Payment Link</Button>
            <Button icon={<HistoryOutlined />} block onClick={onPaymentHistoryModal} style={btnStyle}>View Payment History</Button>
          </div>
        </div>
      </Card>
      {/* Fees editor — only when WO is not locked (no invoice yet) */}
      {workOrder.id && !invoice && (
        <FeesEditor
          entityType="work-orders"
          entityId={workOrder.id}
          editable={!workOrder.id ? false : true}
        />
      )}

      {canSeeCost && pricing.partsCost > 0 && (
        <Card size="small" title="Shop Margins (staff only)" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
            <R label="Total Parts Cost" value={`$${pricing.partsCost.toFixed(2)}`} fs={13} />
            <R label="Total Parts Revenue" value={`$${pricing.partsRevenue.toFixed(2)}`} fs={13} />
            <Divider style={{ margin: '4px 0' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600, color: pricing.partsMargin >= 0 ? '#52c41a' : '#ff4d4f' }}>
              <span>Parts Margin</span><span>${pricing.partsMargin.toFixed(2)} ({pricing.partsRevenue > 0 ? ((pricing.partsMargin / pricing.partsRevenue) * 100).toFixed(1) : '0.0'}%)</span>
            </div>
          </div>
        </Card>
      )}
      {invoice && (
        <Card title={<span style={{ fontSize: 13 }}>Invoice & Receipt</span>} className="shadow-sm" styles={{ body: { padding: pad } }} style={{ overflow: 'hidden' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ backgroundColor: '#f0fdf4', padding: 12, borderRadius: 6, borderLeft: '3px solid #22c55e' }}>
              <div style={{ fontSize: 12, color: '#166534', fontWeight: 600, marginBottom: 4 }}>Work Order Completed</div>
              <div style={{ fontSize: 11, color: '#166534' }}>Invoice generated on {new Date(invoice.invoice_date).toLocaleDateString()}</div>
            </div>
            <div><Text style={{ fontSize: 10, display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#9ca3af', marginBottom: 4 }}>Invoice Number</Text><div style={{ fontSize: 14, fontWeight: 600, fontFamily: 'monospace' }}>{invoice.invoice_number}</div></div>
            <div><Text style={{ fontSize: 10, display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#9ca3af', marginBottom: 4 }}>Status</Text><div style={{ display: 'flex', gap: 4 }}><Tag color="green" style={{ fontSize: 11, padding: '2px 8px' }}>PAID</Tag>{Boolean(invoice.is_archived) && <Tag color="blue" style={{ fontSize: 11, padding: '2px 8px' }}>ARCHIVED</Tag>}</div></div>
            <Divider style={{ margin: '8px 0' }} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <Button type="primary" icon={<MailOutlined />} block onClick={onInvoiceModal} style={btnStyle}>View Invoice Details</Button>
              <Button icon={<MailOutlined />} block onClick={onSendInvoiceEmail} style={btnStyle}>Send to Customer</Button>
              {onSendInvoiceToSelf && (
                <Button icon={<MailOutlined />} block onClick={onSendInvoiceToSelf} style={btnStyle}>Email invoice to me</Button>
              )}
              <Button icon={<PrinterOutlined />} block onClick={onDownloadInvoicePDF} style={btnStyle}>Download PDF</Button>
            </div>
          </div>
        </Card>
      )}
      {signatureData ? (
        <Card title="Customer Approval Signature" size="small" style={{ marginBottom: 16 }}>
          <div style={{ textAlign: 'center', marginBottom: 12 }}><img src={signatureData} alt="Customer signature" style={{ maxWidth: '100%', maxHeight: 120, border: '1px solid #f0f0f0', borderRadius: 4 }} /></div>
          <div style={{ fontSize: 13, color: '#666' }}><div><strong>Signed:</strong> {signedAt ? new Date(signedAt).toLocaleString() : 'N/A'}</div><div><strong>Method:</strong> {signatureType === 'in_person' ? 'Signed in person' : 'Signed remotely'}</div></div>
          {estimateId && <Button icon={<PrinterOutlined />} block onClick={onDownloadApprovalPDF} style={{ marginTop: 12, ...btnStyle }}>Download Approval PDF</Button>}
        </Card>
      ) : estimateId ? (
        <Card title="Customer Approval" size="small" style={{ marginBottom: 16 }}>
          <Button icon={<PrinterOutlined />} block onClick={onDownloadApprovalPDF} style={btnStyle}>Download Approval PDF</Button>
        </Card>
      ) : null}
    </div>
  );
};

export default memo(WorkOrderFinancials);
